import React, { useCallback, useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const DatFileImport = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSearch, setPreviewSearch] = useState('');
  const [previewColumn, setPreviewColumn] = useState('');
  const [visiblePreviewColumns, setVisiblePreviewColumns] = useState([]);
  const [existingHeaderCount, setExistingHeaderCount] = useState(null);
  const [expandedCells, setExpandedCells] = useState(new Set());
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const token = localStorage.getItem('token');

  const loadLatestHeaderCount = useCallback(async (filename) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/data-files/preview/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const body = await res.json();
      setExistingHeaderCount(body.totalFieldCount ?? null);
    } catch {
      setExistingHeaderCount(null);
    }
  }, [token]);

  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/data-files`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Unable to load stored files.');
      }
      const fileList = await res.json();
      setFiles(fileList);
      if (fileList.length > 0) {
        const latest = fileList[0];
        await loadLatestHeaderCount(latest.filename);
      } else {
        setExistingHeaderCount(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingFiles(false);
    }
  }, [loadLatestHeaderCount, token]);

  const readFirstRecordFieldCount = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Unable to read the selected file.'));
      reader.onload = () => {
        const text = reader.result;
        const lines = text.split(/\r?\n/);
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          if (/^(BOF|EOF)\s+PUBLIC\b/i.test(trimmed)) continue;
          if (!trimmed.includes('|')) continue;
          const fieldCount = trimmed.split('|').length;
          return resolve(fieldCount);
        }
        return reject(new Error('Could not find a valid data row in the selected file.'));
      };
      const chunk = file.slice(0, 160000);
      reader.readAsText(chunk);
    });
  };

  const validateSelectedFile = async (file) => {
    if (!file) {
      throw new Error('Please select a .dat file before uploading.');
    }
    if (!file.name.toLowerCase().endsWith('.dat')) {
      throw new Error('Only .dat files are supported.');
    }
    if (existingHeaderCount) {
      const selectedCount = await readFirstRecordFieldCount(file);
      if (selectedCount !== existingHeaderCount) {
        throw new Error(
          `The selected file does not match the existing monthly DAT layout. Expected ${existingHeaderCount} columns but found ${selectedCount}.`
        );
      }
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleFileChange = (event) => {
    setError('');
    setSuccessMessage('');
    setUploadStatus('');
    setUploadProgress(0);
    setSelectedFile(event.target.files[0] || null);
  };

  const handleUpload = async () => {
    try {
      setError('');
      setSuccessMessage('');
      setUploadStatus('Validating file...');
      setUploadProgress(0);
      await validateSelectedFile(selectedFile);
    } catch (validationError) {
      setUploadStatus('');
      setError(validationError.message);
      return;
    }

    setUploadStatus('Uploading...');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/data-files/upload`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        setUploadProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        setUploadStatus('Upload completed successfully.');
        setSuccessMessage('File uploaded and replaced any previous monthly DAT file.');
        setSelectedFile(null);
        setUploadProgress(100);
        await fetchFiles();
      } else {
        let response = {};
        try {
          response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch {
          response = {};
        }
        setError(response.message || 'Upload failed.');
        setUploadStatus('');
      }
    };

    xhr.onerror = () => {
      setError('Upload failed due to a network error.');
      setUploadStatus('');
    };

    xhr.send(formData);
  };

  const handlePreview = async (filename) => {
    setPreviewLoading(true);
    setPreview(null);
    setError('');
    setPreviewSearch('');
    setPreviewColumn('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/data-files/preview/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Could not load file preview.');
      }
      const previewResponse = await res.json();
      setPreview({
        rows: [],
        columns: [],
        previewText: '',
        previewHex: '',
        ...previewResponse,
      });
      setVisiblePreviewColumns(previewResponse.columns || []);
      setExpandedCells(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (filename, displayName) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/data-files/download/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Could not download file.');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = displayName || filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="programs-page">
      <section className="dashboard-section">
        <div className="section-top">
          <h2>Import Monthly DAT File</h2>
          <div className="programs-actions">
            <span className="results-count">Stored Files: {files.length}</span>
            <span className="results-count">Current Columns: {existingHeaderCount ?? '—'}</span>
          </div>
        </div>
        <p className="info-text">
          Upload and preview your monthly DAT submission with the same review workflow styling across the app.
        </p>
      </section>

      <section className="dashboard-section">
        <div className="section-top">
          <h2>Upload File</h2>
        </div>
        <label htmlFor="dat-file-upload" className="filter-label">
          Select .dat file
        </label>
        <input
          id="dat-file-upload"
          type="file"
          accept=".dat"
          onChange={handleFileChange}
        />
        {selectedFile && (
          <p className="file-info">
            Selected file: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
          </p>
        )}
        {files.length > 0 && (
          <p className="info-text">
            A previous monthly DAT file already exists. Uploading a new file will replace the current one.
          </p>
        )}

        <button className="programs-add-btn" type="button" onClick={handleUpload} disabled={!selectedFile}>
          Upload .dat File
        </button>

        {uploadStatus && <p className="status-text">{uploadStatus}</p>}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="progress-bar-wrapper">
            <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
            <span>{uploadProgress}%</span>
          </div>
        )}
        {successMessage && <p className="success-text">{successMessage}</p>}
        {error && <p className="error-text">{error}</p>}
      </section>

      <section className="dashboard-section">
        <div className="section-top">
          <h2>Uploaded Files</h2>
        </div>
        {loadingFiles ? (
          <p>Loading stored files...</p>
        ) : files.length === 0 ? (
          <p className="no-results">No .dat files have been uploaded yet.</p>
        ) : (
          <div className="grants-table-wrapper">
            <table className="grants-table">
              <thead>
                <tr>
                  <th>Saved File</th>
                  <th>Original Name</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.filename}>
                    <td>{file.filename}</td>
                    <td>{file.originalName}</td>
                    <td>{(file.size / 1024 / 1024).toFixed(1)} MB</td>
                    <td>{new Date(file.uploadedAt).toLocaleString()}</td>
                    <td>
                      <div className="programs-actions">
                        <button className="programs-export-btn" type="button" onClick={() => handlePreview(file.filename)}>
                          Preview
                        </button>
                        <button className="programs-export-btn" type="button" onClick={() => handleDownload(file.filename, file.originalName)}>
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {previewLoading && <p>Loading preview...</p>}
      {preview && (
        <section className="dashboard-section">
          <div className="section-top">
            <h2>Preview: {preview.originalName || preview.filename}</h2>
          </div>
          <div className="preview-meta">
              <p>{preview.rowCount || 0} rows loaded for preview.</p>
              <p>{preview.columns?.length || 0} columns detected.</p>
            </div>

            <div className="preview-search-row">
              <label>
                Search rows
                <input
                  type="text"
                  value={previewSearch}
                  onChange={(e) => setPreviewSearch(e.target.value)}
                  placeholder="Search all columns"
                />
              </label>
              <label>
                Column
                <select value={previewColumn} onChange={(e) => setPreviewColumn(e.target.value)}>
                  <option value="">All columns</option>
                  {preview.columns?.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="preview-columns-toggle">
              <p>Toggle visible columns:</p>
              <div className="preview-columns-toggle-grid">
                {preview.columns?.map((col) => (
                  <label key={`toggle-${col}`} className="toggle-checkbox-label">
                    <input
                      type="checkbox"
                      checked={visiblePreviewColumns.includes(col)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setVisiblePreviewColumns((current) => Array.from(new Set([...current, col])));
                        } else {
                          setVisiblePreviewColumns((current) => current.filter((visible) => visible !== col));
                        }
                      }}
                    />
                    {col.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>

            <div className="table-wrapper">
              <table className="data-table preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {visiblePreviewColumns.map((column) => (
                      <th key={column}>
                        {column.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows
                    .filter((row) => {
                      if (!previewSearch.trim()) return true;
                      const search = previewSearch.toLowerCase();
                      const columnIndex = previewColumn ? preview.columns.indexOf(previewColumn) : -1;
                      if (columnIndex >= 0) {
                        return (row[columnIndex] || '').toLowerCase().includes(search);
                      }
                      return row.some((cell) => (cell || '').toString().toLowerCase().includes(search));
                    })
                    .map((row, rowIndex) => {
                      const extractIndex = preview.columns.indexOf('SAM_EXTRACT_CODE');
                      const exclusionIndex = preview.columns.indexOf('EXCLUSION_STATUS');
                      const isExpired = row[extractIndex] === 'E';
                      const isExcluded = row[exclusionIndex] === 'D';
                      return (
                        <tr
                          key={`preview-row-${rowIndex}`}
                          className={isExpired ? 'row-highlight-expired' : isExcluded ? 'row-highlight-excluded' : ''}
                        >
                          <td>{rowIndex + 1}</td>
                          {visiblePreviewColumns.map((column) => {
                            const colIndex = preview.columns.indexOf(column);
                            const cellKey = `r${rowIndex}c${colIndex}`;
                            const rawValue = row[colIndex] || '';
                            const hasMultipleEntries = rawValue.includes(' | ');
                            const expanded = expandedCells.has(cellKey);
                            const displayValue = hasMultipleEntries && !expanded
                              ? `${rawValue.split(' | ')[0]} +${rawValue.split(' | ').length - 1} more`
                              : rawValue;

                            return (
                              <td key={cellKey} className={hasMultipleEntries ? 'collapsible-cell' : ''}>
                                <div className="cell-content">
                                  <span>{displayValue}</span>
                                  {hasMultipleEntries && (
                                    <button
                                      type="button"
                                      className="expand-cell-btn"
                                      onClick={() => {
                                        setExpandedCells((current) => {
                                          const next = new Set(current);
                                          if (next.has(cellKey)) next.delete(cellKey);
                                          else next.add(cellKey);
                                          return next;
                                        });
                                      }}
                                    >
                                      {expanded ? 'Collapse' : 'Expand'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="preview-legend">
              <p><span className="legend-chip expired" />Expired row (SAM_EXTRACT_CODE = E)</p>
              <p><span className="legend-chip excluded" />Excluded row (EXCLUSION_STATUS = D)</p>
            </div>

            <div className="preview-block">
              <strong>Original Raw Text</strong>
              <pre>{preview.previewText || 'No readable text found in the first portion of the file.'}</pre>
            </div>
            <div className="preview-block">
              <strong>Hex Preview</strong>
              <pre>{preview.previewHex}</pre>
            </div>
        </section>
      )}
    </div>
  );
};

export default DatFileImport;
